// ---------------------------------------------------------------------------
// evaluationArticle.js
// Renders the review article that the writing layer produced as ONE
// coherent piece (intro is rendered by the template; this builds the body):
//
//   What it is -> How it works -> Key features -> [owner evidence]
//   -> What users say -> Pricing -> Pros & cons -> Who it's for
//   -> Alternatives -> Before you buy -> Verdict -> FAQ -> Sources
//
// The order follows how a writer builds a review. Variation between pages
// is light and keeps the logic intact: a few neighbouring sections swap,
// features are reordered, pros/cons switch between list and table, and the
// FAQ selection differs. Headings come from the writer (spun per page) and
// never contain the long SEO keyword phrase.
//
// Sections only render when they have real content. Calls to action:
// three, worded differently, placed after "What it is", after
// "Pros & cons" and after the verdict, never inside other sections.
// ---------------------------------------------------------------------------
import { htmlTextEscape as esc, htmlAttrEscape as attr } from './escaping.js';

const FALLBACK_HEADINGS = {
  what_it_is: 'What {P} is',
  how_it_works: 'How it works',
  features: 'Key features',
  user_feedback: 'What users are saying',
  pricing: 'Pricing and value',
  pros_cons: 'Pros and cons',
  who_for: 'Who {P} is for',
  alternatives: 'Alternatives to consider',
  before_you_buy: 'Before you buy',
  verdict: 'Final verdict',
  faq: 'Frequently asked questions',
};

const CTA = {
  top: [
    ['Want to see the current plans first?', 'Check {P} on the official site'],
    ['Prefer to look at the product yourself?', 'Visit the official {P} site'],
    ['Curious about the details?', 'See what {P} includes'],
  ],
  middle: [
    ['If the trade-offs above work for you,', 'compare the {P} plans'],
    ['Weighing it up?', 'Review the current {P} pricing and terms'],
    ['Before deciding, it\'s worth reading the details:', 'see what each {P} plan includes'],
  ],
  end: [
    ['If {P} sounds like the right fit,', 'check the latest pricing and refund policy'],
    ['Ready to decide?', 'Go to the official {P} site'],
    ['If you go ahead, confirm the current terms first:', 'view {P} on the official site'],
  ],
};

const safeUrl = (value) => {
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) ? url.href : null;
  } catch { return null; }
};

const hostOf = (url) => { try { return new URL(url).hostname.replace(/^(www|m)\./, ''); } catch { return url; } };

const shuffle = (items, rng) => {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng._next() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

const paras = (list) => (list || []).filter(Boolean).map((t) => `<p>${esc(t)}</p>`).join('');
const ul = (items) => (items?.length ? `<ul>${items.map((i) => `<li>${esc(i)}</li>`).join('')}</ul>` : '');

// ---------------------------------------------------------------------------
// "YouTube video description" style: short and scannable, in the order a
// viewer reads a description under a video:
//   [official link] -> [Chapters, only from real timestamps]
//   -> [My hands-on test, only from owner evidence] -> Key takeaways
//   -> Pros and cons -> Pricing -> Who it's for -> What users say
//   -> Bottom line -> Quick questions -> Links and sources -> hashtags
// The hook and summary are rendered by the template as the intro.
// ---------------------------------------------------------------------------
const buildDescriptionHtml = (article, product, ctx = {}) => {
  const d = article.description;
  const { factSheet = null, evidence = null, officialUrl = '', rng } = ctx;
  const random = rng || { _next: Math.random };
  const name = String(product || 'it');
  const pick = (list) => list[Math.floor(random._next() * list.length)];
  const official = safeUrl(officialUrl || factSheet?.official_site || '');
  const affiliate = Boolean(evidence?.affiliate);
  const rel = affiliate ? 'sponsored nofollow noopener' : 'nofollow noopener';
  const reviews = factSheet?.user_reviews;
  const moments = evidence?.videoMoments || [];
  const ownerEvidence = Boolean(evidence && (evidence.testNotes || evidence.metricNotes || evidence.docExcerpts
    || evidence.links?.length || evidence.images?.length));
  const h2 = (options) => `<h2>${esc(pick(options).replace(/\{P\}/g, name))}</h2>`;
  const parts = [];

  // Link near the top, the way video descriptions do it.
  if (official) {
    parts.push(`<p class="review-cta">${esc(pick(['Official site:', `${name} official site:`, 'Check it out here:']))} <a href="${attr(official)}" rel="${rel}" target="_blank">${esc(hostOf(official))}</a></p>`);
  }
  if (affiliate) parts.push('<p class="review-disclosure"><em>Disclosure: I may earn a commission if you buy through links on this page. It doesn\'t change my opinion.</em></p>');

  // Chapters: only real timestamps typed in by the owner.
  if (moments.length) {
    parts.push(h2(['Chapters', 'Timestamps', 'In this video']) + `<ul>${moments.map((m) => `<li><strong>${esc(m.time)}</strong> ${esc(m.description)}</li>`).join('')}</ul>`);
  }

  // Hands-on section: only when the owner supplied evidence.
  if (ownerEvidence && (d.experience?.length || evidence.images?.length || evidence.testNotes || evidence.metricNotes)) {
    const figures = (evidence.images || []).map((img) => `<figure><img src="${attr(img.url)}" alt="${attr(img.caption || `${name} screenshot`)}" loading="lazy"/>${img.caption ? `<figcaption>${esc(img.caption)}</figcaption>` : ''}</figure>`).join('');
    parts.push(h2(['My hands-on test', 'What I tested', `Testing {P} myself`])
      + (d.experience?.length ? paras(d.experience) : paras([evidence.testNotes, evidence.metricNotes]))
      + figures
      + (evidence.docExcerpts ? `<blockquote>${esc(evidence.docExcerpts)}</blockquote>` : ''));
  }

  if (d.takeaways?.length) parts.push(h2(['Key takeaways', 'The short version', 'What you need to know']) + ul(d.takeaways));

  if (d.pros?.length || d.cons?.length) {
    parts.push(h2(['Pros and cons', 'The good and the bad', 'What I like and what I don\'t'])
      + (d.pros?.length ? `<h3>Pros</h3>${ul(d.pros)}` : '') + (d.cons?.length ? `<h3>Cons</h3>${ul(d.cons)}` : ''));
  }

  if (d.pricing) parts.push(h2(['Pricing', 'What it costs', `{P} pricing`]) + `<p>${esc(d.pricing)}</p>`);

  if (d.whoFor || d.whoShouldSkip) {
    parts.push(h2(['Who it\'s for', `Is {P} right for you?`, 'Who should get it'])
      + (d.whoFor ? `<p><strong>Good fit:</strong> ${esc(d.whoFor)}</p>` : '')
      + (d.whoShouldSkip ? `<p><strong>Skip it if:</strong> ${esc(d.whoShouldSkip)}</p>` : ''));
  }

  // User feedback only when real reviews were verified by the research step.
  if (reviews?.found && d.userFeedback) parts.push(h2(['What users say', 'What other users report', 'User feedback']) + `<p>${esc(d.userFeedback)}</p>`);

  if (d.verdict) parts.push(h2(['Bottom line', 'My verdict', 'Final verdict']) + `<p>${esc(d.verdict)}</p>`);

  if (d.faq?.length) parts.push(h2(['Quick questions', 'FAQ', 'Questions I get asked']) + d.faq.map((f) => `<h3>${esc(f.q)}</h3><p>${esc(f.a)}</p>`).join(''));

  // Links and sources.
  const links = [];
  if (official) links.push(`<li><a href="${attr(official)}" rel="${rel}" target="_blank">${esc(name)} official website</a></li>`);
  for (const url of evidence?.links || []) links.push(`<li><a href="${attr(url)}" rel="noopener" target="_blank">${esc(hostOf(url))}</a></li>`);
  for (const s of (reviews?.found ? reviews.sources : [])) {
    const url = safeUrl(s.url);
    if (url) links.push(`<li><a href="${attr(url)}" rel="nofollow noopener" target="_blank">${esc(s.site || hostOf(url))}</a></li>`);
  }
  const note = ownerEvidence
    ? 'This review is based on my own use of the product, the official website and user discussions.'
    : `This review is based on the official website${reviews?.found ? ' and independent user discussions' : ''}, not on hands-on testing.`;
  parts.push(h2(['Links and sources', 'Links mentioned', 'Sources']) + (links.length ? `<ul>${links.join('')}</ul>` : '') + `<p><em>${note}</em></p>`);

  if (d.hashtags?.length) parts.push(`<p class="review-hashtags">${d.hashtags.map((tag) => esc(tag)).join(' ')}</p>`);

  return `<section class="evaluation video-description">${parts.join('')}</section>`;
};

export const buildEvaluationHtml = (article, product, ctx = {}) => {
  if (!article) return '';
  if (article.description) return buildDescriptionHtml(article, product, ctx);
  const { factSheet = null, evidence = null, officialUrl = '', rng } = ctx;
  const random = rng || { _next: Math.random };
  const name = String(product || 'it');
  const pick = (list) => list[Math.floor(random._next() * list.length)];
  const h2 = (key, text) => `<h2>${esc(text || FALLBACK_HEADINGS[key].replace(/\{P\}/g, name))}</h2>`;
  const official = safeUrl(officialUrl || factSheet?.official_site || '');
  const affiliate = Boolean(evidence?.affiliate);
  const reviews = factSheet?.user_reviews;
  const ownerEvidence = Boolean(evidence && (evidence.testNotes || evidence.metricNotes || evidence.docExcerpts
    || evidence.links?.length || evidence.images?.length));
  const has = (s) => Boolean(s?.paragraphs?.length);

  const sections = {
    what_it_is: () => has(article.what_it_is) && h2('what_it_is', article.what_it_is.heading) + paras(article.what_it_is.paragraphs),

    how_it_works: () => {
      const s = article.how_it_works;
      if (!has(s) && !s?.steps?.length) return '';
      return h2('how_it_works', s.heading) + paras(s.paragraphs)
        + (s.steps?.length ? `<ol>${s.steps.map((step) => `<li>${esc(step)}</li>`).join('')}</ol>` : '');
    },

    features: () => {
      const f = article.features;
      if (!f?.items?.length) return '';
      return h2('features', f.heading) + (f.lead ? `<p>${esc(f.lead)}</p>` : '')
        + shuffle(f.items, random).map((item) => `<h3>${esc(item.heading)}</h3>${paras(item.paragraphs)}`).join('');
    },

    evidence: () => {
      if (!ownerEvidence) return '';
      const figures = (evidence.images || []).map((img) => `<figure><img src="${attr(img.url)}" alt="${attr(img.caption || `${name} screenshot`)}" loading="lazy"/>${img.caption ? `<figcaption>${esc(img.caption)}</figcaption>` : ''}</figure>`).join('');
      return `<h2>${esc(pick(['Our hands-on evidence', 'What our testing showed', 'From our own use']))}</h2>`
        + (article.evidenceCommentary ? `<p>${esc(article.evidenceCommentary)}</p>` : '') + figures
        + (evidence.testNotes ? `<h3>What we tested</h3><p>${esc(evidence.testNotes)}</p>` : '')
        + (evidence.metricNotes ? `<h3>Measured results</h3><p>${esc(evidence.metricNotes)}</p>` : '')
        + (evidence.docExcerpts ? `<h3>From the documentation</h3><blockquote>${esc(evidence.docExcerpts)}</blockquote>` : '');
    },

    videoMoments: () => {
      const moments = evidence?.videoMoments || [];
      if (!moments.length) return '';
      return `<h2>${esc(pick(['What the video shows', 'Inside the video', 'Key moments in the demo']))}</h2><ul>${
        moments.map((m) => `<li><strong>Around ${esc(m.time)}:</strong> ${esc(m.description)}</li>`).join('')}</ul>`;
    },

    // Narrative user feedback only when real reviews were verified.
    user_feedback: () => (reviews?.found && has(article.user_feedback))
      && h2('user_feedback', article.user_feedback.heading) + paras(article.user_feedback.paragraphs),

    pricing: () => has(article.pricing) && h2('pricing', article.pricing.heading) + paras(article.pricing.paragraphs),

    pros_cons: () => {
      const { pros = [], cons = [], heading } = article.pros_cons || {};
      if (!pros.length && !cons.length) return '';
      if (random._next() < 0.5 && pros.length && cons.length) {
        const rows = Math.max(pros.length, cons.length);
        return h2('pros_cons', heading) + `<table><thead><tr><th>Pros</th><th>Cons</th></tr></thead><tbody>${
          Array.from({ length: rows }, (_, i) => `<tr><td>${esc(pros[i] || '')}</td><td>${esc(cons[i] || '')}</td></tr>`).join('')}</tbody></table>`;
      }
      return h2('pros_cons', heading) + (pros.length ? `<h3>Pros</h3>${ul(pros)}` : '') + (cons.length ? `<h3>Cons</h3>${ul(cons)}` : '');
    },

    who_for: () => {
      const w = article.who_for;
      if (!has(w) && !w?.good_fit?.length) return '';
      return h2('who_for', w.heading) + paras(w.paragraphs)
        + (w.good_fit?.length ? `<h3>A good fit if…</h3>${ul(w.good_fit)}` : '')
        + (w.poor_fit?.length ? `<h3>Probably not for you if…</h3>${ul(w.poor_fit)}` : '');
    },

    alternatives: () => has(article.alternatives) && h2('alternatives', article.alternatives.heading) + paras(article.alternatives.paragraphs),

    before_you_buy: () => article.before_you_buy?.items?.length
      && h2('before_you_buy', article.before_you_buy.heading) + ul(article.before_you_buy.items),

    verdict: () => has(article.verdict) && h2('verdict', article.verdict.heading) + paras(article.verdict.paragraphs),

    faq: () => article.faq?.items?.length
      && h2('faq', article.faq.heading) + article.faq.items.map((f) => `<h3>${esc(f.q)}</h3><p>${esc(f.a)}</p>`).join(''),
  };

  // ── Writer's order, with light per-page variation ──
  const swap = (a, b) => (random._next() < 0.5 ? [a, b] : [b, a]);
  const order = [
    'what_it_is', 'how_it_works', 'features', 'evidence', 'videoMoments',
    ...swap('user_feedback', 'pricing'),
    'pros_cons',
    ...swap('who_for', 'alternatives'),
    'before_you_buy', 'verdict', 'faq',
  ];

  // ── Calls to action: three, worded differently, in commercial spots ──
  const cta = (slot) => {
    const [lead, linkText] = pick(CTA[slot]).map((t) => t.replace(/\{P\}/g, name));
    const rel = affiliate ? 'sponsored nofollow noopener' : 'nofollow noopener';
    const link = official ? `<a href="${attr(official)}" rel="${rel}" target="_blank">${esc(linkText)}</a>` : esc(linkText.replace(/^./, (c) => c.toLowerCase()));
    return `<p class="review-cta">${esc(lead)} ${link}.</p>`;
  };
  const ctaAfter = { what_it_is: 'top', pros_cons: 'middle', verdict: 'end' };

  const parts = [];
  if (affiliate) parts.push('<p class="review-disclosure"><em>Disclosure: we may earn a commission if you buy through links on this page. It doesn\'t change our assessment.</em></p>');
  for (const key of order) {
    const html = sections[key]();
    if (!html) continue;
    parts.push(html);
    if (ctaAfter[key]) parts.push(cta(ctaAfter[key]));
  }
  if (parts.length <= (affiliate ? 1 : 0)) return '';

  // ── Sources: one compact list at the end ──
  const sourceItems = [];
  if (official) sourceItems.push(`<li><a href="${attr(official)}" rel="nofollow noopener" target="_blank">${esc(name)} official website</a></li>`);
  for (const s of (reviews?.found ? reviews.sources : [])) {
    const url = safeUrl(s.url);
    if (url) sourceItems.push(`<li><a href="${attr(url)}" rel="nofollow noopener" target="_blank">${esc(s.site || hostOf(url))}</a></li>`);
  }
  const note = ownerEvidence
    ? 'This review draws on the official website, user discussions and our own use of the product.'
    : `This review draws on the official website${reviews?.found ? ' and independent user discussions' : ''}. We have not tested ${esc(name)} hands-on.`;
  parts.push(`<h2>${esc(pick(['Sources', 'Sources and further reading', 'Where this information comes from']))}</h2>`
    + (sourceItems.length ? `<ul>${sourceItems.join('')}</ul>` : '') + `<p><em>${note}</em></p>`);

  return `<section class="evaluation">${parts.join('')}</section>`;
};
